(ns app.domain.group.permit-type-config.model
  (:require [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [potpuri.core :as p]
            [schema.core :as s]))

(s/defschema PermitTypeAndCategory
  "A tuple of permit type and category"
  [(s/one s/Keyword "permit")
   (s/one s/Keyword "category")])

(def primary-permit-types
  "Main permit types, i.e. permit types which have their own authority group"
  #{:permit-type/B
    :permit-type/CE
    :permit-type/CR
    :permit-type/L
    :permit-type/LU
    :permit-type/P
    :permit-type/PW
    :permit-type/SW
    :permit-type/PWO})

(def permit-type->icon-path
  {:permit-type/B  "/asset/img/landing-v2-icon-permitting.svg"
   :permit-type/P  "/asset/img/landing-v2-icon-planning.svg"
   :permit-type/PW "/asset/img/landing-v2-icon-public-works.svg"
   :permit-type/L  "/asset/img/landing-v2-icon-licensing.svg"
   :permit-type/CR "/asset/img/landing-v2-icon-licensing.svg"
   :permit-type/LU "/asset/img/landing-v2-icon-land-permitting.svg"
   :permit-type/CE "/asset/img/landing-v2-icon-enforcement.svg"})

(def additional-permit-types
  {:permit-type/B [:permit-type/BS]
   :permit-type/L [:permit-type/LS]})

(defn permit-type-matches-as-primary-for
  "Return true `primary-permit-type` can have an additional permit with type
  `additional-permit-type`."
  [primary-permit-type additional-permit-type]
  (-> additional-permit-types
      (get primary-permit-type)
      set
      (contains? additional-permit-type)))

(def additional-permit-type?
  (->> additional-permit-types
       vals
       (apply concat)
       set))

(def configurable-hidden-types
  "Permit types that aren't shown in application wizard but can be configured like normal permit types.
  The permit type is not a primary type nor a supplemental type."
  #{:permit-type/PI})

(def editable-permit-types
  "Permit types that a super admin can directly add/remove/configure for a group, i.e. primary permit
  types plus configurable-hidden-types (e.g. :permit-type/PI). Excludes additional/supplemental permit
  types (e.g. :permit-type/BS, :permit-type/LS), which are always derived from their primary type."
  (set/union primary-permit-types configurable-hidden-types))

(def application-only-types
  "Permit types where execution phase is not used at all.

  TODO this is a bit hacky, would be preferable to read it from the
       state machine definition.
       Difficult to do currently since due to the logic of falling back on the default FSM"
  #{:permit-type/BS
    :permit-type/L
    :permit-type/CR})

(def execution-only-types
  #{:permit-type/CE
    :permit-type/LS
    :permit-type/PWO})

(def no-pre-consultation-types
  #{:permit-type/BS
    :permit-type/L
    :permit-type/LS
    :permit-type/CE
    :permit-type/CR
    :permit-type/PI
    :permit-type/PWO})

(def application-types
  (set/difference primary-permit-types execution-only-types))

(def permit-types-with-pre-consultation
  (set/difference application-types no-pre-consultation-types))

(defn application-permit-type? [ptc]
  (-> ptc :permit-type-config/permit-type application-types))

(def never-location-types
  "Permit types that never have a workspace location"
  #{:permit-type/CR})

(def permit-types-without-mandatory-location
  "Permit types where location can be omitted (based on config etc)"
  #{:permit-type/L
    :permit-type/LS
    :permit-type/CR})

(def permit-types-with-multiple-work-types
  "Permit types that can have multiple work-types selected on a single application"
  #{:permit-type/CR})

(def default-categories
  "The default enabled categories for a municipality based on permit type.
  :all = all categories, :none = no categories, or an explicit set of category keywords."
  {:permit-type/B   :all
   :permit-type/CE  :all
   :permit-type/PW  #{:category/driveway
                      :category/encroachment
                      :category/excavation-grade-fill
                      :category/heavy-haul
                      :category/parking-lot
                      :category/pw-sewer
                      :category/special-event
                      :category/utilities}
   :permit-type/P   :all
   :permit-type/L   :all
   :permit-type/LU  :all
   :permit-type/CR  :all
   :permit-type/PWO :all
   :permit-type/SW  :none
   :permit-type/PI  :none})

(def permit-types-with-reviews
  #{:permit-type/L
    :permit-type/CR
    :permit-type/B
    :permit-type/P
    :permit-type/LU
    :permit-type/PW
    :permit-type/SW})

(def permit-types-with-data-import
  #{:permit-type/B :permit-type/PW :permit-type/CE
    :permit-type/L :permit-type/SW :permit-type/CR
    :permit-type/P :permit-type/LU})

(def permit-types-with-fees-and-payments
  #{:permit-type/B :permit-type/CE :permit-type/P :permit-type/SW
    :permit-type/CR :permit-type/L :permit-type/PW :permit-type/LU})

(def permit-types-with-circulations
  #{:permit-type/B :permit-type/P :permit-type/LU :permit-type/PW :permit-type/L :permit-type/SW :permit-type/PI :permit-type/PWO})

(def permit-types-with-qualifications
  "Permit types that use qualifications"
  #{:permit-type/B
    :permit-type/P
    :permit-type/LU
    :permit-type/SW
    :permit-type/PI
    :permit-type/PW
    :permit-type/CE
    :permit-type/L
    :permit-type/PWO})

(def permit-types-with-default-qualifications
  "Permit types that have default qualifications"
  (disj permit-types-with-qualifications :permit-type/PWO))

(def permit-types-with-committees
  #{:permit-type/P :permit-type/LU})

(def permit-types-with-row-layers
  #{:permit-type/PW :permit-type/PWO})

(def permit-types-with-permit-sign-off
  "Permit types that can make use of permit sign-off functionality"
  #{:permit-type/B :permit-type/BS :permit-type/PW :permit-type/LU :permit-type/SW :permit-type/P})

(def permit-types-with-renewal
  "Permit types with renewal and the associated notifications"
  #{:permit-type/L :permit-type/CR})

(def permit-types-with-properties
  #{:permit-type/B :permit-type/P :permit-type/LU :permit-type/PW :permit-type/L :permit-type/CE :permit-type/SW :permit-type/PI :permit-type/PWO})

(def hidden-permit-types
  "Permit types hidden from the application wizard"
  #{:permit-type/CE :permit-type/PI :permit-type/PWO})

(def building-permit-types
  #{:permit-type/B :permit-type/BS :permit-type/SW})

(def permit-types-with-permit-templates
  #{:permit-type/B :permit-type/L :permit-type/PW :permit-type/LU :permit-type/P :permit-type/CR :permit-type/SW})

(def permit-types-with-inspections
  #{:permit-type/B :permit-type/PW :permit-type/CE :permit-type/L :permit-type/SW :permit-type/LU :permit-type/P :permit-type/PI :permit-type/PWO})

(def permit-types-with-required-category
  #{:permit-type/CE :permit-type/PWO})

(def permit-types-with-closure-categories
  #{:permit-type/CE :permit-type/PWO})

(def permit-types-with-complaints
  #{:permit-type/CE :permit-type/PWO})

(defn complaint-permit-type? [ptc]
  (-> ptc :permit-type-config/permit-type permit-types-with-complaints))

(def permit-types-with-non-owner-violation
  #{:permit-type/CE})

(def permit-types-without-inspection-orders-available
  #{:permit-type/PWO})

(def permit-types-with-inspection-orders-not-enabled
  "Permit types with orders unavailable or not enabled by default"
  (set/union permit-types-without-inspection-orders-available
             #{:permit-type/CE :permit-type/L}))

(def permit-types-without-multiple-inspection-booking-applicant
  #{:permit-type/PWO})

(def permit-types-without-applicant-visible-inspections
  #{:permit-type/CE})

(def permit-types-without-comply-by-date
  #{:permit-type/PWO})

(def permit-types-without-refunds
  #{:permit-type/PWO})

(def permit-types-with-editable-subtypes
  #{:permit-type/CE :permit-type/PI :permit-type/PWO})

(def permit-types-with-express-issuance
  #{:permit-type/B :permit-type/CR :permit-type/L :permit-type/PW :permit-type/P :permit-type/LU})

(defn filter-default-category-map [permit-type->categories]
  (->> permit-type->categories
       (p/map-entries
         (fn [[permit-type all-categories]]
           (let [default (permit-type default-categories)]
             [permit-type
              (case default
                :none #{}
                :all  (set all-categories)
                default)])))
       (p/remove-vals nil?)))

(defn filter-default-category-seq [permit-type all-categories]
  (->> (hash-map permit-type all-categories)
       filter-default-category-map
       permit-type))

(defmethod i18n-model/get-localized-string-attrs :entity.type/permit-type-config [_]
  [:permit-type-config/permit-type-descriptions])

(def ^{:arglists '([permit-type-config lang])} permit-type-config-description
  "Get permit-type-config description on a selected language"
  (i18n-model/translation-fallback-fn :permit-type-config/permit-type-descriptions))

;; TODO remove after full report support
(def superdomain-allowed-permit-types
  #{:permit-type/B
    :permit-type/CE})
